import React, { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import qs from 'querystring';
import ApolloClient from 'apollo-client';
import {Auth } from 'aws-amplify';
import { ApolloProvider } from 'react-apollo';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Form from 'components/form';
import Button from 'components/common/Button';
import Input from 'components/form/Input';
import Select from 'components/form/Select';
import Radio from 'components/form/Radio';
import PhoneInput from 'components/form/PhoneInput';
import useSnack from 'hooks/useSnack';
import { useMutation } from 'react-apollo';
import { CREATE_LEAD } from 'graphql/referral/leads';
import { HttpLink } from 'apollo-link-http';
import { InMemoryCache } from 'apollo-cache-inmemory';

const FormItem = Form.FormItem;

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_HGE_ENDPOINT,
});

const cache = new InMemoryCache({});
const client = new ApolloClient({
  link: httpLink,
  cache,
});

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto',
    height: '100%',
  },
}));

const LeadForm = ({ form, location }) => {
  const classes = useStyles();
  const snack = useSnack();
  const [submitted, setSubmitted] = useState(false);
  const [addLead] = useMutation(CREATE_LEAD, {
    client
  });
  const { getFieldDecorator, validateFields } = form;
  const query = qs.parse(location.search.substr(1));

  useEffect(() => {
    Auth.signOut();
  }, []);

  const onSubmit = () => {
    return validateFields().then(
      (values) => {
        if (query.ref) {
          values.referral_code = query.ref;
        }
        return addLead({
          variables: {
            data: values,
          },
        }).then(
          () => {
            setSubmitted(true);
          },
          (e) => {
            snack.error(e.message);
          },
        );
      },
      (e) => snack.error('Please validate your input'),
    );
  };

  return (
    <ApolloProvider client={client}>
      <Box className={classes.root} py={5}>
        <Helmet title="Schedule Your Free Counseling Session" />
        <Container>
          <Grid container justify="center">
            <Grid item xs={12} md={8}>
              <Box textAlign="center" mb={{ xs: 5, sm: 8 }}>
                <Typography variant="h3" paragraph>
                  Schedule Your Free Counseling Session
                </Typography>
                <Typography color="textSecondary">
                  We work with students anywhere in the United States. To
                  request a free 30 minute online counseling session, please
                  complete the form below. We will get back to you within 24
                  hours.
                </Typography>
              </Box>
            </Grid>
            {submitted ? (
              <Grid item xs={12} md={6} component={Box} textAlign="center">
                <Typography color="primary" variant="h5">
                  Thank you!
                </Typography>
                <Typography>You'll hear from us soon.</Typography>
              </Grid>
            ) : (
              <Grid item xs={12} md={6}>
                <Box>
                  <Form>
                    <FormItem>
                      {getFieldDecorator('first_name', {
                        rules: [
                          {
                            required: true,
                            message: 'Please enter first name',
                          },
                        ],
                      })(<Input label="First Name" />)}
                    </FormItem>
                    <FormItem>
                      {getFieldDecorator('last_name', {
                        rules: [
                          {
                            required: true,
                            message: 'Please enter last name',
                          },
                        ],
                      })(<Input label="Last Name" />)}
                    </FormItem>
                    <FormItem>
                      {getFieldDecorator('lead_role', {
                        rules: [
                          {
                            required: true,
                            message: 'Please select',
                          },
                        ],
                      })(
                        <Radio
                          options={[
                            { label: 'Parent', value: 'parent' },
                            { label: 'Student', value: 'student' },
                          ]}
                          label="I’m a..."
                        />,
                      )}
                    </FormItem>
                    <FormItem>
                      {getFieldDecorator('student_grade', {
                        rules: [
                          {
                            required: true,
                            message: 'Please select',
                          },
                        ],
                      })(
                        <Radio
                          options={[
                            {
                              label: 'Class of 2021',
                              value: 'senior-2021',
                            },
                            {
                              label: 'Class of 2022',
                              value: 'junior-2022',
                            },
                            {
                              label: 'Class of 2023',
                              value: 'sophomore-2023',
                            },
                            {
                              label: 'Class of 2024',
                              value: 'sophomore-2024',
                            },
                            {
                              label: 'Class of 2025',
                              value: 'sophomore-2025',
                            },
                          ]}
                          label="Student Grade"
                        />,
                      )}
                    </FormItem>
                    <FormItem>
                      {getFieldDecorator('school_name', {
                        rules: [
                          {
                            required: true,
                            message: 'Please enter school name',
                          },
                        ],
                      })(<Input label="High School Name" />)}
                    </FormItem>
                    <FormItem>
                      {getFieldDecorator('email', {
                        rules: [
                          {
                            required: true,
                            message: 'Please enter email',
                          },
                          {
                            type: 'email',
                            message: 'Please enter valid email address',
                          },
                        ],
                      })(<Input type="email" label="Email" />)}
                    </FormItem>
                    <FormItem>
                      {getFieldDecorator('phone', {
                        rules: [
                          {
                            required: true,
                            message: 'Please enter phone number',
                          },
                        ],
                      })(<PhoneInput label="Phone" />)}
                    </FormItem>
                    <FormItem>
                      {getFieldDecorator('lead_source')(
                        <Select
                          label="How did you find us? (optional)"
                          isMulti={false}
                          transformValue
                          options={[
                            { label: 'Search Engine', value: 'Search Engine' },
                            {
                              label: 'Family or Friend',
                              value: 'Family or Friend',
                            },
                            { label: 'Other', value: 'Other' },
                          ]}
                        />,
                      )}
                    </FormItem>
                    <FormItem>
                      {getFieldDecorator('referred_by')(
                        <Input label="Referrer Name (if applicable)" />,
                      )}
                    </FormItem>
                    { query.ref && (
                      <Box>
                        <Input label="Referral Code" disabled value={query.ref} />
                      </Box>
                    )}
                    <Box py={3}>
                      <Divider />
                    </Box>
                    <Typography variant="small" color="textSecondary">
                      By submitting your email you’ll automatically be
                      subscribed to WeAdmit’s Admissions Newsletter! You can
                      opt-out from the newsletter at anytime.
                    </Typography>
                    <Box py={3} textAlign="center">
                      <Button
                        onClick={onSubmit}
                        variant="contained"
                        color="primary"
                        size="large"
                      >
                        Submit Form
                      </Button>
                    </Box>
                  </Form>
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>
    </ApolloProvider>
  );
};

export default Form.create()(LeadForm);
